/**
 * Integrations
 *
 * Hands-on hub for wiring and testing customer integrations. Documentation
 * lives on docs.simkura.com — this page is the management/testing surface:
 *
 *   - REST API  — API key management
 *   - Webhooks  — webhook management
 *   - MQTT      — bridge management
 *   - Testing   — fire synthetic events (admins; events hit their own company)
 *
 * Tab selection is URL-driven (`?tab=`) so deep-links survive nav and the old
 * /developer redirect can land users on a specific surface.
 *
 * Access: company admins (super admins pick a company). The OAuth apps and
 * per-user Connected Apps surfaces were retired Sept 2026 — API keys are the
 * integration credential.
 *
 * Company picker logic is shared across the tabs: super admin sees the
 * dropdown in the page header and the picked company scopes EVERY management
 * section. Company admin is pinned to their own company.
 */

import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import styled from '@emotion/styled';
import { useAuth } from '../context/AuthContext';
import { isSuperAdmin, isCompanyAdmin } from '../services/users';
import { getCompanies } from '../services/companies';
import {
  PageContainer,
  PageHeader,
  PageTitle,
  Select,
  Label,
  HelperText,
  EmptyState,
  MutedText,
} from '../components/styled';
import {
  GatewayApiKeys,
  Webhooks,
  MqttIntegrations,
  Testing,
} from './integrations/panels';
import { SegmentedTabs } from '../components/SegmentedTabs';

type IntegrationsTab = 'rest' | 'webhooks' | 'mqtt' | 'testing';

const TOP_TABS: { id: IntegrationsTab; label: string }[] = [
  { id: 'rest',     label: 'REST API' },
  { id: 'webhooks', label: 'Webhooks' },
  { id: 'mqtt',     label: 'MQTT' },
  { id: 'testing',  label: 'Testing' },
];

function isIntegrationsTab(v: string | null): v is IntegrationsTab {
  return v === 'rest' || v === 'webhooks' || v === 'mqtt' || v === 'testing';
}

const HeaderRow = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
`;

const DocsLink = styled.a`
  font-size: ${({ theme }) => theme.font.size.sm};
  font-weight: ${({ theme }) => theme.font.weight.medium};
  color: ${({ theme }) => theme.accent.primary};
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
`;

const CompanyPickerRow = styled.div`
  display: flex;
  align-items: flex-end;
  gap: ${({ theme }) => theme.spacing(3)};
  margin-bottom: ${({ theme }) => theme.spacing(5)};
`;

const CompanyPickerField = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(1)};
  min-width: 260px;
`;

// White background so the picker stands out from the canvas-soft page bg
// (shared Select defaults to background.secondary, which is the same tint).
const CompanyPickerSelect = styled(Select)`
  background: ${({ theme }) => theme.background.primary};
`;

// One-line framing under the REST tab: which credential fits which caller.
const TabCaption = styled.p`
  margin: ${({ theme }) => theme.spacing(0, 0, 5, 0)};
  max-width: 720px;
  font-size: ${({ theme }) => theme.font.size.sm};
  color: ${({ theme }) => theme.font.color.secondary};
  line-height: ${({ theme }) => theme.font.lineHeight.lg};
`;

function Integrations() {
  const { user } = useAuth();
  const [params, setParams] = useSearchParams();

  const isSuper = isSuperAdmin(user);
  const isAdmin = isCompanyAdmin(user);

  // Active tab from URL; falls back to the default. Writing back to the URL
  // keeps deep links + the back button honest. Stale deep-links (e.g. the
  // removed connected-apps / oauth surfaces) fall back to REST.
  const urlTab = params.get('tab');
  const activeTab: IntegrationsTab = isIntegrationsTab(urlTab) ? urlTab : 'rest';

  function setTab(next: IntegrationsTab) {
    const p = new URLSearchParams(params);
    p.set('tab', next);
    p.delete('sub'); // legacy REST sub-tab param
    setParams(p, { replace: false });
  }

  // Company picker — shared across all tabs, only rendered for super admin.
  const userCompanyId = user?.company_id;
  const [pickedCompanyId, setPickedCompanyId] = useState<string>('');

  const { data: companiesData, isLoading: companiesLoading } = useQuery({
    queryKey: ['integrations', 'companies'],
    queryFn: getCompanies,
    enabled: isSuper,
  });
  const companies = useMemo(() => companiesData?.companies ?? [], [companiesData]);

  useEffect(() => {
    if (isSuper && !pickedCompanyId && companies.length > 0) {
      setPickedCompanyId(String(companies[0].id));
    }
  }, [isSuper, pickedCompanyId, companies]);

  const companyId = isSuper ? pickedCompanyId : String(userCompanyId ?? '');

  // The whole hub is company-admin territory now that the per-user Connected
  // Apps surface is gone.
  if (!isAdmin) {
    return (
      <PageContainer>
        <PageHeader>
          <HeaderRow style={{ width: '100%' }}>
            <PageTitle>Integrations</PageTitle>
            <DocsLink href="https://docs.simkura.com" target="_blank" rel="noreferrer">
              API documentation →
            </DocsLink>
          </HeaderRow>
        </PageHeader>
        <EmptyState>
          <p>Integration management is available to company admins.</p>
          <MutedText>Contact your administrator for access.</MutedText>
        </EmptyState>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <PageHeader>
        <HeaderRow style={{ width: '100%' }}>
          <PageTitle>Integrations</PageTitle>
          <DocsLink href="https://docs.simkura.com" target="_blank" rel="noreferrer">
            API documentation →
          </DocsLink>
        </HeaderRow>
      </PageHeader>

      {isSuper && (
        <CompanyPickerRow>
          <CompanyPickerField>
            <Label>Company</Label>
            <CompanyPickerSelect
              value={pickedCompanyId}
              onChange={(e) => setPickedCompanyId(e.target.value)}
              disabled={companiesLoading}
            >
              <option value="">— Select a company —</option>
              {companies.map((c) => (
                <option key={c.id} value={String(c.id)}>{c.name}</option>
              ))}
            </CompanyPickerSelect>
            <HelperText>
              Pick a company to scope every integration section — same view the company admin sees.
            </HelperText>
          </CompanyPickerField>
        </CompanyPickerRow>
      )}

      <SegmentedTabs
        tabs={TOP_TABS}
        activeId={activeTab}
        onChange={(id) => setTab(id as IntegrationsTab)}
      />

      {/* Every tab needs a companyId. If super admin hasn't picked yet, prompt. */}
      {!companyId && isSuper && (
        <EmptyState>
          <p>Pick a company above to view its integrations.</p>
        </EmptyState>
      )}

      {!companyId && !isSuper && (
        <EmptyState>
          <p>No company associated with your account.</p>
          <MutedText>Contact your administrator.</MutedText>
        </EmptyState>
      )}

      {companyId && activeTab === 'rest' && (
        <>
          <TabCaption>
            For your <strong>own backend</strong> calling Simkura directly — one long-lived key,
            scoped to your company. Restrict a key to specific devices from the Devices column.
          </TabCaption>
          <GatewayApiKeys companyId={companyId} />
        </>
      )}

      {companyId && activeTab === 'webhooks' && <Webhooks companyId={companyId} />}

      {companyId && activeTab === 'mqtt' && <MqttIntegrations companyId={companyId} />}

      {companyId && activeTab === 'testing' && <Testing companyId={companyId} />}
    </PageContainer>
  );
}

export default Integrations;
